import { Badge, Button, Card, EmptyState, Input, Spinner, useToast } from '@ohear/ui'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { AdminGuard } from '../../features/admin/AdminGuard'
import { AdminNav } from '../../features/admin/AdminNav'
import {
  type AdminFailedJob,
  useFailedJobs,
  useReplayFailedJob,
  useResolveFailedJob,
} from '../../lib/api/admin'

export const Route = createFileRoute('/admin/failed-jobs')({
  component: FailedJobsPage,
})

const PAGE_SIZE = 50

function FailedJobsPage() {
  const [page, setPage] = useState(1)
  const [jobName, setJobName] = useState('')
  const [unresolved, setUnresolved] = useState(true)

  const filters = {
    page,
    page_size: PAGE_SIZE,
    unresolved,
    ...(jobName ? { job_name: jobName } : {}),
  }
  const jobs = useFailedJobs(filters)
  const replay = useReplayFailedJob()
  const resolve = useResolveFailedJob()
  const toast = useToast()

  const items = jobs.data?.items ?? []
  const total = jobs.data?.total ?? 0
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const onReplay = (row: AdminFailedJob) => {
    replay.mutate(row.id, {
      onSuccess: (data) => {
        toast.show({
          severity: data.dispatched ? 'success' : 'warn',
          title: data.dispatched
            ? `Replayed ${row.job_name}`
            : `Queued ${row.job_name} (no Modal endpoint configured)`,
        })
      },
      onError: (err) => {
        toast.show({
          severity: 'error',
          title: 'Replay failed',
          description: err instanceof Error ? err.message : 'Unknown error',
        })
      },
    })
  }

  const onResolve = (row: AdminFailedJob) => {
    resolve.mutate(row.id, {
      onSuccess: () => toast.show({ severity: 'success', title: 'Marked resolved' }),
      onError: (err) =>
        toast.show({
          severity: 'error',
          title: 'Resolve failed',
          description: err instanceof Error ? err.message : 'Unknown error',
        }),
    })
  }

  return (
    <AdminGuard>
      <section className="flex flex-col gap-3" aria-labelledby="admin-failed-title">
        <header className="flex flex-col gap-1">
          <div className="text-[12px] uppercase tracking-wide text-[var(--text-3)]">
            Platform admin
          </div>
          <h1 id="admin-failed-title" className="text-[22px] font-semibold tracking-tight m-0">
            Failed jobs
          </h1>
          <p className="text-[13px] text-[var(--text-2)] m-0">
            Modal jobs that exhausted their retry budget. Replay re-dispatches with the original
            input; Resolve dismisses the row without retrying.
          </p>
        </header>

        <AdminNav />

        <Card>
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault()
              setPage(1)
            }}
          >
            <label className="flex flex-col gap-1 text-[13px] flex-1 min-w-[180px]">
              <span className="font-medium">Job name</span>
              <Input
                value={jobName}
                onChange={(e) => setJobName(e.target.value)}
                placeholder="e.g. stem_render"
              />
            </label>
            <label className="flex items-center gap-2 text-[13px]">
              <input
                type="checkbox"
                checked={unresolved}
                onChange={(e) => {
                  setUnresolved(e.target.checked)
                  setPage(1)
                }}
              />
              Unresolved only
            </label>
            <Button type="submit" variant="ghost">
              Apply
            </Button>
          </form>
        </Card>

        <Card dense>
          {jobs.isLoading ? (
            <div className="flex items-center gap-2 text-[var(--text-2)]">
              <Spinner /> Loading failed jobs...
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              title="No failed jobs"
              description="The dead-letter queue is empty for this filter."
            />
          ) : (
            <div className="overflow-x-auto -mx-3 -my-3">
              <table
                className="w-full text-[13px] border-separate"
                style={{ borderSpacing: 0 }}
                aria-label="Failed jobs"
              >
                <thead
                  className="sticky top-0 z-10 text-left text-[11px] uppercase tracking-wide text-[var(--text-2)]"
                  style={{ background: 'var(--bg-3)' }}
                >
                  <tr>
                    <th className="px-3 py-2 font-medium">Job</th>
                    <th className="px-3 py-2 font-medium">Attempts</th>
                    <th className="px-3 py-2 font-medium">Last error</th>
                    <th className="px-3 py-2 font-medium">Last attempt</th>
                    <th className="px-3 py-2 font-medium">State</th>
                    <th className="px-3 py-2 font-medium" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr key={row.id} className="align-top transition-colors hover:bg-[var(--bg-3)]">
                      <td
                        className="px-3 py-2 font-medium border-t"
                        style={{ borderColor: 'var(--border)' }}
                      >
                        {row.job_name}
                      </td>
                      <td
                        className="px-3 py-2 tabular-nums border-t"
                        style={{ borderColor: 'var(--border)' }}
                      >
                        {row.attempt_count}
                      </td>
                      <td
                        className="px-3 py-2 text-[12px] text-[var(--text-2)] max-w-[420px] border-t"
                        style={{ borderColor: 'var(--border)' }}
                      >
                        <code className="break-all whitespace-pre-wrap">
                          {row.error_message ?? '(no message)'}
                        </code>
                      </td>
                      <td
                        className="px-3 py-2 text-[var(--text-2)] tabular-nums border-t"
                        style={{ borderColor: 'var(--border)' }}
                      >
                        {new Date(row.last_attempt_at).toLocaleString()}
                      </td>
                      <td className="px-3 py-2 border-t" style={{ borderColor: 'var(--border)' }}>
                        {row.resolved_at ? (
                          <Badge variant="green">resolved</Badge>
                        ) : (
                          <Badge variant="amber">pending</Badge>
                        )}
                      </td>
                      <td
                        className="px-3 py-2 text-right whitespace-nowrap border-t"
                        style={{ borderColor: 'var(--border)' }}
                      >
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onReplay(row)}
                          loading={replay.isPending && replay.variables === row.id}
                          disabled={!!row.resolved_at}
                        >
                          Replay
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onResolve(row)}
                          loading={resolve.isPending && resolve.variables === row.id}
                          disabled={!!row.resolved_at}
                        >
                          Resolve
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <div className="flex items-center justify-between text-[12px] text-[var(--text-2)]">
          <span>
            Page {page} of {lastPage} - {total} total
          </span>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="ghost"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={page >= lastPage}
              onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      </section>
    </AdminGuard>
  )
}
